<template>
  <div
    class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-white"
  >
    <div class="rounded-t mb-0 px-4 py-3 border-0 bg-emerald-500">
      <div class="flex flex-wrap items-center justify-between">
        <h3 class="font-semibold text-lg text-white">Employees List</h3>
        <div class="flex gap-2">
          <button
            @click="inviteEmployee"
            class="bg-white text-blue-600 text-sm font-bold px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150"
          >
            <i class="fas fa-envelope mr-1"></i> Invite Employee
          </button>
          <button
            @click="openModal"
            class="bg-white text-emerald-600 text-sm font-bold px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150"
          >
            <i class="fas fa-plus mr-1"></i> Add Employee
          </button>
        </div>
      </div>
    </div>

    <div class="block w-full overflow-x-auto">
      <table class="items-center w-full bg-transparent border-collapse">
        <thead>
          <tr>
            <th class="text-white px-6 py-3 bg-blueGray-600 text-left text-xs font-medium uppercase tracking-wider">Name</th>
            <th class="text-white px-6 py-3 bg-blueGray-600 text-left text-xs font-medium uppercase tracking-wider">Employee ID</th>
            <th class="text-white px-6 py-3 bg-blueGray-600 text-left text-xs font-medium uppercase tracking-wider">Email</th>
            <th class="text-white px-6 py-3 bg-blueGray-600 text-left text-xs font-medium uppercase tracking-wider">Phone</th>
            <th class="text-white px-6 py-3 bg-blueGray-600 text-left text-xs font-medium uppercase tracking-wider">Position</th>
            <th class="text-white px-6 py-3 bg-blueGray-600 text-left text-xs font-medium uppercase tracking-wider">Hired Date</th>
            <th class="text-white px-6 py-3 bg-blueGray-600 text-left text-xs font-medium uppercase tracking-wider">Separation Date</th>
            <th class="text-white px-6 py-3 bg-blueGray-600 text-center text-xs font-medium uppercase tracking-wider">Status</th>
            <th class="text-white px-6 py-3 bg-blueGray-600 text-center text-xs font-medium uppercase tracking-wider">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="employee in sortedEmployees" :key="employee.id">
            <td class="px-6 py-4 text-sm font-medium text-gray-900">
              {{ employee.employee.first_name }} {{ employee.employee.last_name }}
            </td>
            <td class="px-6 py-4 text-sm">
              <span v-if="employee.employee.employee_code" class="font-mono font-bold bg-gray-100 text-gray-700 px-2 py-1 rounded border border-gray-200">{{ employee.employee.employee_code }}</span>
              <span v-else class="text-gray-400">—</span>
            </td>
            <td class="px-6 py-4 text-sm">{{ employee.employee.email }}</td>
            <td class="px-6 py-4 text-sm">{{ employee.employee.phone_number }}</td>
            <td class="px-6 py-4 text-sm">{{ employee.position }}</td>
            <td class="px-6 py-4 text-sm">{{ employee.hired_date }}</td>
            <td class="px-6 py-4 text-sm">{{ employee.separation_date || 'N/A' }}</td>
            <td class="px-6 py-4 text-sm text-center">
              <span
                :class="{
                  'text-emerald-600 font-bold': employee.status_label === 'Active',
                  'text-yellow-600 font-bold': employee.status_label === 'Pending for approval',
                  'text-red-500 font-bold': employee.status_label === 'Declined' || employee.status_label === 'Terminated',
                }"
              >{{ employee.status_label }}</span>
            </td>
            <td class="px-6 py-4 text-sm text-center">
              <button
                v-if="!employee.separation_date"
                @click="terminateEmployee(employee.id)"
                class="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded hover:bg-red-600"
              >
                Terminate
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <hire-employee-modal :is-open="isModalOpen" @close="closeModal" />
  </div>
</template>

<script>
import HireEmployeeModal from "@/components/Modal/HireEmployeeModal.vue";
import axios from "axios";
import Swal from "sweetalert2/dist/sweetalert2.js";
import "sweetalert2/dist/sweetalert2.min.css";

export default {
  components: {
    HireEmployeeModal,
  },
  data() {
    return {
      isModalOpen: false,
      employees_data: [],
    };
  },
  computed: {
    sortedEmployees() {
      return [...this.employees_data].sort((a) => (a.separation_date ? 1 : -1));
    },
  },
  methods: {
    openGmailCompose(email, signupUrl) {
      const subject = "Sign in link | BFMS";
      const body = [
        "Hello,",
        "",
        "Please use this sign in link:",
        signupUrl,
        "",
        "Blessed FMS",
      ].join("\n");

      const gmailUrl =
        `https://mail.google.com/mail/u/0/?view=cm&fs=1&tf=1` +
        `&to=${encodeURIComponent(email)}` +
        `&su=${encodeURIComponent(subject)}` +
        `&body=${encodeURIComponent(body)}`;

      window.open(gmailUrl, "_blank", "noopener,noreferrer");
    },

    async inviteEmployee() {
      const { value: email } = await Swal.fire({
        title: "Invite Employee",
        text: "Enter the employee's email address. They will receive a signup link.",
        input: "email",
        inputPlaceholder: "employee@example.com",
        showCancelButton: true,
        confirmButtonText: "Send Invite",
        confirmButtonColor: "#3b82f6",
        inputValidator: (value) => {
          if (!value) return "Please enter an email address.";
        },
      });

      if (!email) return;

      Swal.fire({
        title: "Sending invitation...",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });

      try {
        const token = localStorage.getItem("token");

        const params_data = {
          email: email,
          token: token,
        };

        const res = await axios.post(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/invite_employee`,
          params_data
        );

        Swal.close();

        if (res.data.signup_url) {
          this.openGmailCompose(email, res.data.signup_url);

          Swal.fire({
            icon: "success",
            title: "Gmail opened",
            html: `
              <p class="text-left text-sm mb-2">
                A Gmail compose tab was opened with the sign-in link prefilled.
              </p>
              <p class="text-left text-xs" style="word-break: break-all;">
                ${res.data.signup_url}
              </p>
            `,
            confirmButtonText: "OK",
          });
          return;
        }

        if (res.data.email_sent === true) {
          Swal.fire({
            icon: "success",
            title: "Invitation sent!",
            text: res.data.message || `Signup link sent to ${email}`,
          });
          return;
        }

        Swal.fire({
          icon: "warning",
          title: "Invitation created",
          text: res.data.message || "The invitation was created, but no signup link was returned.",
        });
      } catch (error) {
        console.error("Invite employee error:", error);

        let msg = "Network error. Please try again.";

        if (error.response && error.response.data) {
          msg =
            error.response.data.error ||
            error.response.data.message ||
            msg;
        }

        Swal.fire({
          icon: "error",
          title: "Failed to send invitation",
          text: msg,
        });
      }
    },

    openModal() {
      this.isModalOpen = true;
    },

    closeModal() {
      this.isModalOpen = false;
      this.fetch_client_employees();
    },

    async fetch_client_employees() {
      const token = localStorage.getItem("token");

      const params_data = {
        token: token,
      };

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/fetch_client_employee`,
          params_data
        );
        this.employees_data = response.data.data;
      } catch (error) {
        console.error("Backend error:", error);
      }
    },

    async terminateEmployee(id) {
      const token = localStorage.getItem("token");

      const params_data = {
        token: token,
        id: id,
      };

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/terminate_employee`,
          params_data
        );
        if (response.data.status == 200) {
          Swal.fire({ title: response.data.message, icon: "success" });
          this.fetch_client_employees();
        } else {
          Swal.fire({ title: response.data.message, icon: "error" });
        }
      } catch (error) {
        console.error("Backend error:", error);
      }
    },
  },

  mounted() {
    this.fetch_client_employees();
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1040;
}

.modal-container {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
  z-index: 1050;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
}

.modal-header button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
}
</style>
